<template>
  <Dashboard title="Training Session" :no-padding="user.access_level < 1">
    <h1 class="text-text-primary font-medium text-[28px] flex-grow flex items-center gap-2 mb-4 flex items-baseline">
      <router-link to="/sessions">{{ $t('training.sessions.timeline') }}</router-link>

      <span v-if="trainingSessionLoading" class="inline-block bg-gray-800 self-end mb-2 rounded-lg ml-4 w-32 h-8" />

      <ChevronRightIcon v-if="trainingSession?.created_at" class="w-3 h-3 mx-1" />
      <span class="text-gray-100 font-normal text-xl" v-if="trainingSession?.created_at">
        {{ moment(trainingSession.created_at).calendar(null, {
          sameDay: '[Today]', lastDay: '[Yesterday]', lastWeek: 'MMMM Do, YYYY', sameElse: 'MMMM Do, YYYY',
        }) }}
      </span>

      <Server v-if="trainingSession" :trainingSession="trainingSession" />
    </h1>

    <div class="bg-gray-900 rounded-lg p-2 mb-3 h-[180px]" v-if="trainingSessionLoading" />
    <div class="bg-gray-900 rounded-lg p-2 mb-3" v-else-if="trainingSession?.activities?.length > 0">
      <StatisticCharts :statistics="statistics" @selectedMetricChange="selectedMetricChange" :singleSession="true" />
    </div>

    <div v-if="trainingSessionLoading">
      <h2 class="p-6 bg-gray-800 rounded-t-lg" />
      <div class="p-6 border-t border-gray-900 bg-gray-800" />
      <div class="h-2 rounded-b-lg mb-3 bg-gray-800" />
    </div>
    <i18n-t scope="global" v-else-if="notFound" keypath="training.session_details.not_found" tag="div" class="bg-gray-900 p-4 text-warning-400 text-xs tracking-[1.5px]">
      <template #link><router-link to="/sessions" class="underline">{{ $t('training.session_details.not_found_link') }}</router-link></template>
    </i18n-t>
    <div v-else-if="trainingSession?.activities?.length > 0" v-for="activity in trainingSession.activities">
      <Activity :live="trainingSession.live" :activity="activity" :modCategories="modCategories" :selectedMetric="selectedMetric" />
    </div>
    <div v-else class="bg-gray-900 p-4 text-text-secondary text-xs tracking-[1.5px]">
      {{ $t('training.shared.server_ready_hint') }}
    </div>
  </Dashboard>
</template>

<script setup>
import moment from 'moment';
import axios from 'axios';
import {
  ref, onMounted, onBeforeUnmount, getCurrentInstance, computed,
} from 'vue';
import { useAuth } from '@websanova/vue-auth/src/v3.js';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { useI18n } from 'vue-i18n';

import { ChevronRightIcon } from '@heroicons/vue/24/outline';
import Dashboard from '../../components/layouts/DashboardLayout.vue';
import Activity from './Activity.vue';
import Server from './Server.vue';
import StatisticCharts from './StatisticCharts.vue';

const { t } = useI18n();
const auth = useAuth();
const user = ref(auth.user());
const route = useRoute();
const modCategories = ref([]);
const trainingSessionLoading = ref(true);
const notFound = ref(false);
const trainingSession = ref(null);
const selectedMetric = ref(null);

const cable = getCurrentInstance().appContext.config.globalProperties.$cable;
let websocket = null;
let subscription = null;

useHead({ title: t('training.session_details.page_title') });

function getModCategories() {
  axios('/mod_categories').then(({ data }) => {
    modCategories.value = data.mod_categories;
  });
}

function loadTrainingSession() {
  axios.get(`/training_sessions/${route.params.uuid}`).then(({ data }) => {
    trainingSession.value = data;
    trainingSessionLoading.value = false;
    if (data.live) {
      useHead({ title: t('training.session_details.page_title_live') });
    } else {
      useHead({ title: t('training.session_details.page_title') });
    }
  }).catch((error) => {
    if (error.response.status === 404) {
      notFound.value = true;
      trainingSessionLoading.value = false;
    }
  });
}

function subscribeToLiveTrainingSessionUpdate() {
  if (!websocket) {
    websocket = cable.connect();
  }
  if (!subscription) {
    subscription = websocket.subscriptions.create(
      { channel: 'TrainingSessionChannel', uuid: route.params.uuid },
      {
        received: (data) => {
          if (data.event === 'training_session_update') {
            loadTrainingSession();
          }
        },
      },
    );
  }
}

function unsubscribeToLiveTrainingSessionUpdate() {
  if (websocket) {
    websocket.disconnect();
    websocket = null;
  }
  subscription = null;
}

const statistics = computed(() => trainingSession.value?.activities
  ?.flatMap((activity) => activity.tasks.map((task) => {
    if (task.statistics?.length > 0) {
      const statistic = task.statistics[0];
      return { ...statistic, task };
    }
    return null;
  })
    .filter((el) => el !== null))
  .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));

function selectedMetricChange(newMetric) {
  selectedMetric.value = newMetric;
}

onMounted(() => {
  getModCategories();
  loadTrainingSession();
  subscribeToLiveTrainingSessionUpdate();
});

onBeforeUnmount(() => {
  unsubscribeToLiveTrainingSessionUpdate();
});
</script>
